import PropTypes from 'prop-types';
import { Icon } from '@iconify/react';

export default function ToastItem({ toast, position, handleCloseButton }) {
  const colorClassname = {
    success: 'bg-green-500/30 text-green-900 dark:text-green-200',
    warning: 'bg-yellow-500/30 text-yellow-900 dark:text-yellow-200',
    danger: 'bg-red-500/30 text-red-800 dark:text-red-200',
    primary: 'bg-indigo-500/30 text-indigo-800 dark:text-indigo-200',
    secondary:
      'bg-gray-200/30 dark:bg-gray-800/30 text-gray-800 dark:text-gray-200',
  };

  const toastIcon = {
    success: 'heroicons:check-circle-20-solid',
    warning: 'heroicons:exclamation-circle-20-solid',
    danger: 'heroicons:x-circle-20-solid',
    primary: 'heroicons:information-circle-20-solid',
    secondary: 'heroicons:bell-20-solid',
  };

  const animationClassName = {
    topLeft: '[animation:toast-in-top_0.5s] sm:[animation:toast-in-left_0.5s]',
    topRight:
      '[animation:toast-in-top_0.5s] sm:[animation:toast-in-right_0.5s]',
    bottomLeft:
      '[animation:toast-in-bottom_0.5s] sm:[animation:toast-in-left_0.5s]',
    bottomRight:
      '[animation:toast-in-bottom_0.5s] sm:[animation:toast-in-right_0.5s]',
  };

  return (
    <div
      key={toast.id}
      className={`group z-50 my-2 mx-auto box-border w-fit rounded-2xl bg-gray-100 text-sm transition-transform duration-500 ease-in-out supports-[backdrop-filter]:bg-gray-100/30 supports-[backdrop-filter]:backdrop-blur dark:bg-gray-900 dark:supports-[backdrop-filter]:bg-gray-900/30 ${animationClassName[position]}`}
    >
      <div
        className={`mx-auto flex max-w-xs flex-row items-start justify-between space-x-6 rounded-xl ${
          toast.title && toast.description ? 'p-4' : 'px-3 py-2'
        } opacity-80 shadow-lg group-hover:opacity-100 ${
          colorClassname[toast.type]
        }`}
      >
        <div
          className={`flex flex-row items-center ${
            toast.title && toast.description ? 'gap-3' : 'gap-1'
          }`}
        >
          <div className="shrink-0">
            <Icon
              className={
                toast.title && toast.description ? 'h-10 w-10' : 'h-5 w-5'
              }
              icon={toastIcon[toast.type]}
            />
          </div>
          <div className="max-h-24 overflow-y-auto [word-break:break-word]">
            {toast.title && (
              <div className="text-lg font-medium leading-6 line-clamp-1 hover:line-clamp-none">
                {toast.title}
              </div>
            )}
            <p className="leading-4 line-clamp-1 group-hover:line-clamp-none">
              {toast.description}
            </p>
          </div>
        </div>
        <div className="relative">
          <button
            onClick={handleCloseButton}
            className="absolute top-1 right-1 opacity-10 group-hover:opacity-60"
          >
            <Icon icon="heroicons:x-mark-20-solid" />
          </button>
        </div>
      </div>
    </div>
  );
}

ToastItem.propTypes = {
  /** Toast item object */
  toast: PropTypes.shape({
    /** unique ID for toast item */
    id: PropTypes.string.isRequired,
    /** toast item type */
    type: PropTypes.oneOf([
      'success',
      'warning',
      'danger',
      'primary',
      'secondary',
    ]),
    /** toast item title */
    title: PropTypes.string,
    /** toast item description */
    description: PropTypes.string,
  }),
  /** toast item position to define animation keyframe */
  position: PropTypes.oneOf([
    'topLeft',
    'topRight',
    'bottomLeft',
    'bottomRight',
  ]).isRequired,
  /** function to close toast item */
  handleCloseButton: PropTypes.func.isRequired,
};
